import React, { useCallback, useMemo, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useResponsive } from '../../contexts/ResponsiveContext';
import { SpecialLinkHandler, transformContentHtml } from '../../utils/infoBannerContentTransformer';

interface InfoBannerProps {
    /** Unique identifier for the banner, used for localStorage */
    identifier: string;
    content: string;
}

const InfoBanner: React.FC<InfoBannerProps> = ({ identifier, content }) => {
    const [visible, setVisible] = useState(() => localStorage.getItem(identifier) !== 'closed');
    const { responsiveWidth } = useResponsive();

    const handleDismiss = useCallback(() => {
        localStorage.setItem(identifier, 'closed');
        setVisible(false);
    }, [identifier]);

    // No more special custom handlers needed anymore, Cookie Settings is handled by default
    const handlers: SpecialLinkHandler = useMemo(() => ({}), []);

    const parsedContent = useMemo(
        () => transformContentHtml(content, handlers),
        [content, handlers]
    );

    if (!visible) return null;

    return (
        <Box sx={{ width: '100%' }} data-testid="info-banner-box">
            <Box
                sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'flex-start',
                    backgroundColor: '#FAE1D8',
                    border: '1px solid #E0CFCB',
                    borderRadius: '8px',
                    padding: '1rem',
                    width: responsiveWidth,
                    gap: '0.75rem',
                    margin: '0 auto 1rem auto',
                    fontFamily: 'Poppins',
                }}
                data-testid="info-banner-inner-box"
            >
                <IconButton
                    onClick={handleDismiss}
                    sx={{
                        position: 'absolute',
                        top: '2px',
                        right: '2px',
                        color: 'black',
                        padding: '2px',
                        width: '20px',
                        height: '20px',
                        minWidth: 0,
                        border: '2px solid #ccc',
                        borderRadius: '50%',
                        transition: 'background-color 0.2s',
                        '&:hover': {
                            backgroundColor: '#f0f0f0',
                        },
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>

                <InfoOutlinedIcon sx={{ color: '#4B4B4B', marginTop: '3px' }} />

                <Typography
                    component="div"
                    variant="body2"
                    sx={{ color: '#191A1B', fontWeight: 400, fontSize: '16px' }}
                >
                    {parsedContent}
                </Typography>
            </Box>
        </Box>
    );
};

export default InfoBanner;
